import { api } from '@/app/api'
import type { Paginated, ResourceResponse, ListParams } from '@/lib/types'
import type {
  Notification, NotificationPreference,
  StorageFile, StorageStats,
  AuditLogDetail,
} from './polish-types'

export const polishApi = api.injectEndpoints({
  endpoints: (build) => ({
    // ── Notifications ──────────────────────────────────────
    listNotifications: build.query<Paginated<Notification>, ListParams & { category?: string; unread?: boolean }>({
      query: (params) => ({ url: '/v1/notifications', params }),
      providesTags: ['User'],
    }),
    notificationUnreadCount: build.query<{ data: { count: number } }, void>({
      query: () => '/v1/notifications/unread-count',
      providesTags: ['User'],
    }),
    notificationPreferences: build.query<{ data: NotificationPreference[] }, void>({
      query: () => '/v1/notifications/preferences',
      providesTags: ['User'],
    }),
    setNotificationPreference: build.mutation<unknown, Partial<NotificationPreference> & { category: string }>({
      query: (body) => ({ url: '/v1/notifications/preferences', method: 'POST', body }),
      invalidatesTags: ['User'],
    }),
    markNotificationRead: build.mutation<unknown, string>({
      query: (id) => ({ url: `/v1/notifications/${id}/read`, method: 'POST' }),
      invalidatesTags: ['User'],
    }),
    markAllNotificationsRead: build.mutation<unknown, void>({
      query: () => ({ url: '/v1/notifications/read-all', method: 'POST' }),
      invalidatesTags: ['User'],
    }),

    // ── Storage ────────────────────────────────────────────
    storageStats: build.query<{ data: StorageStats }, void>({
      query: () => '/v1/storage/stats',
      providesTags: ['Module'],
    }),
    listStorageFiles: build.query<Paginated<StorageFile>, ListParams & { category?: string; visibility?: string; attachable_type?: string }>({
      query: (params) => ({ url: '/v1/storage', params }),
      providesTags: ['Module'],
    }),
    getStorageFile: build.query<ResourceResponse<StorageFile>, string>({
      query: (id) => `/v1/storage/${id}`,
    }),
    deleteStorageFile: build.mutation<void, string>({
      query: (id) => ({ url: `/v1/storage/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Module'],
    }),
    issueStorageSignedUrl: build.mutation<{ data: { url: string; expires_at: string } }, { id: string; expires_in_minutes?: number }>({
      query: ({ id, ...body }) => ({ url: `/v1/storage/${id}/signed-url`, method: 'POST', body }),
    }),

    // ── Audit log detail ───────────────────────────────────
    auditLogDetail: build.query<{ data: AuditLogDetail }, number>({
      query: (id) => `/v1/audit-logs/${id}`,
    }),
  }),
  overrideExisting: false,
})

export const {
  useListNotificationsQuery, useNotificationUnreadCountQuery,
  useNotificationPreferencesQuery, useSetNotificationPreferenceMutation,
  useMarkNotificationReadMutation, useMarkAllNotificationsReadMutation,
  useStorageStatsQuery, useListStorageFilesQuery, useGetStorageFileQuery,
  useDeleteStorageFileMutation, useIssueStorageSignedUrlMutation,
  useAuditLogDetailQuery,
} = polishApi
